import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, Form, Input, Button, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  registerHrefWithNext,
} from '../utils/onboardingNavigation';
import { resolvePostLoginPath } from '../utils/postLoginDestination';
import { LOGIN_IDENTIFIER_RULES } from '../utils/loginIdentifier';
import { DevRosterLoginPicker } from '../components/Auth/DevRosterLoginPicker';
import {
  EmailMfaChallengeForm,
  type EmailMfaStep,
} from '../components/Auth/EmailMfaChallengeForm';
import {
  clearPendingMfaToken,
  getPendingMfaToken,
  pendingMfaStepFromToken,
} from '../services/authTokenRefresh';
import { mfaLoginEmailHint, parseMfaResendResult, applyMfaResendFeedback } from '../utils/mfaEmailHints';

export const LoginPage: React.FC = () => {
  const { login, verifyMfaOtp, resendMfaOtp, isAuthenticated, loadUserCompanies } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState<EmailMfaStep | null>(() =>
    pendingMfaStepFromToken(getPendingMfaToken())
  );
  const [resendHint, setResendHint] = useState<string | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState<number | null>(null);
  const [resendCooldownToken, setResendCooldownToken] = useState(0);
  const registered = searchParams.get('registered') === '1';
  const nextRaw = searchParams.get('next');
  const passwordReset = searchParams.get('reset') === '1';
  const registerHref = registerHrefWithNext(nextRaw);

  const navigateAfterAuth = async () => {
    const companies = await loadUserCompanies();
    navigate(resolvePostLoginPath(nextRaw, companies, { registered }), { replace: true });
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      const companies = await loadUserCompanies();
      if (!cancelled) {
        navigate(resolvePostLoginPath(nextRaw, companies, { registered }), { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loadUserCompanies, navigate, nextRaw, registered]);

  const onFinish = async (values: { identifier: string; password: string }) => {
    setLoading(true);
    setResendHint(null);
    setResendCooldownSeconds(null);
    try {
      const result = await login(values.identifier.trim(), values.password);
      if (result.mfaRequired) {
        setMfaStep(result.mfaStep);
        setResendHint(mfaLoginEmailHint(result.mfaEmailSent));
        return;
      }
      await navigateAfterAuth();
    } catch (err: unknown) {
      form.setFields([
        {
          name: 'password',
          errors: [getApiErrorMessage(err, 'Login failed. Check your email and password.')],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      await verifyMfaOtp(otp);
      await navigateAfterAuth();
    } finally {
      setLoading(false);
    }
  };

  const clearResendCooldown = () => {
    setResendCooldownSeconds(null);
  };

  const onResend = async () => {
    setResendLoading(true);
    setResendHint(null);
    try {
      const result = await resendMfaOtp();
      applyMfaResendFeedback(parseMfaResendResult(result), {
        setHint: setResendHint,
        setCooldownSeconds: setResendCooldownSeconds,
        bumpCooldownToken: () => setResendCooldownToken((t) => t + 1),
      });
    } catch (err: unknown) {
      setResendCooldownSeconds(null);
      setResendHint(getApiErrorMessage(err, 'Could not resend code. Sign in again.'));
    } finally {
      setResendLoading(false);
    }
  };

  const backToPassword = () => {
    clearPendingMfaToken();
    setMfaStep(null);
    setResendHint(null);
    setResendCooldownSeconds(null);
  };

  return (
    <div style={{ maxWidth: 440, margin: '48px auto', padding: 24 }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          {mfaStep ? 'Verify your email' : 'Sign in'}
        </Typography.Title>
        {registered && !mfaStep && (
          <Typography.Paragraph type="success" style={{ marginBottom: 16 }}>
            Account created. Sign in with your email and password to continue to intake.
          </Typography.Paragraph>
        )}
        {registered && mfaStep && (
          <Typography.Paragraph type="success" style={{ marginBottom: 16 }}>
            Enter the code we emailed you — you won&apos;t need to sign in again after this.
          </Typography.Paragraph>
        )}
        {passwordReset && !mfaStep && (
          <Typography.Paragraph type="success" style={{ marginBottom: 16 }}>
            Password updated. Sign in with your new password.
          </Typography.Paragraph>
        )}

        {mfaStep ? (
          <EmailMfaChallengeForm
            key={mfaStep}
            mfaStep={mfaStep}
            loading={loading}
            resendLoading={resendLoading}
            resendHint={resendHint}
            resendCooldownSeconds={resendCooldownSeconds}
            resendCooldownToken={resendCooldownToken}
            onResendCooldownFinished={clearResendCooldown}
            onVerify={onVerifyOtp}
            onResend={onResend}
            onBack={backToPassword}
          />
        ) : (
          <>
            <DevRosterLoginPicker
              onSelect={(identifier) => form.setFieldsValue({ identifier })}
            />
            <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
              <Form.Item name="identifier" label="Email or username" rules={[...LOGIN_IDENTIFIER_RULES]}>
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Email or username"
                  size="large"
                  autoComplete="username"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter your password' },
                  { min: 8, message: 'Use at least 8 characters' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  size="large"
                  autoComplete="current-password"
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block size="large">
                  Sign in
                </Button>
              </Form.Item>
            </Form>
            <div style={{ textAlign: 'center' }}>
              <Link to="/forgot-password">Forgot password?</Link>
              {' · '}
              <Link to={registerHref}>Create an account</Link>
              {' · '}
              <Link to="/">Home</Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
